import { observable, computed, action, IObservableValue } from 'mobx';
import { runSaga } from 'redux-saga';
import {
	WebGivingPage,
	WebGivingPageViewModel,
} from '../pages';
import { userActionChannel, Channel } from '../../Shared/utils/user-action-channel';
import { webGivingSaga, IWebGivingSagaContext, sagaDataService } from '../webgiving-saga';
import * as Models from '../webgiving-generated';
import { WebGivingViewModel, WebGivingPersonModel } from '../webgiving-generated';
import { defaultAppState } from '../webgiving-app-state';
import '../../reportUnhandledRejection';
import { HistoryAdapter } from '../utils/history-adapter';
import { WebGivingUserAction } from '../webgiving-user-actions';
import { GoogleAnalyticsWrapper } from '../infrastructure/google-analytics-wrapper';
import { defaultErrorMessage, getErrorInfo, ConfirmGiftPostError } from '../utils/error-utils';
import { NotificationViewModel } from '../components/notification/notification';
import { WebGivingErrorType } from '../components/error-message/error-message';
import { PostError } from '../../Shared/utils/ajax-client';
import { ModalType } from '../modals/modal-type';
import { getUserRememberedAmount, clearUserRemembered } from '../utils/local-storage';
import { ScrollStateStore, getScrollStateStore } from '../../Shared/utils/scroll-state-store';
import { getLanguageWithFallback } from '../utils/language-utils';

export interface IWebGivingMainViewModel {
	readonly currentPageViewModel: WebGivingPageViewModel;
	readonly currentPage: WebGivingPage;
	readonly person: WebGivingPersonModel | null;
	readonly visibleModal: ModalType | null;
	readonly uiConfiguration: Models.WebGivingUIConfiguration;
	isAppLoading: boolean;
	resendCodeDisabled: IObservableValue<boolean>;
	hasTranslationEnabled: boolean;

	viewData: WebGivingViewModel;
	scrollStateStore: ScrollStateStore;
	theme: Models.Theme;
	brandingPackage: Models.BrandingPackage | null;
	applePayAvailable: boolean;

	openPage(viewModel: WebGivingPageViewModel): void;
	redirectToRedirectUrlFromSuccessPage(url: string): void;
	openModal(modalType: ModalType): void;
	closeModal(): void;
	showNotification(notification: NotificationViewModel): void;
	showAnimation(showAnimation: Models.ThankYouAnimationType): void;
	hideAnimation(): void;
	setIsAccountDetailsOpen(value: boolean): void;
	setCurrentPageShouldBeVisible(): void;
	failedToLoadLanguage(notification: NotificationViewModel, language: Models.Language): void;
	setAppLoading(isLoading: boolean): void;
	setApplePayAvailable(available: boolean): void;
}

export class WebGivingMainViewModel implements IWebGivingMainViewModel {
	@computed
	get currentPageViewModel(): WebGivingPageViewModel {
		return this._currentPageViewModel;
	}

	@computed
	get currentPage(): WebGivingPage {
		return this._currentPageViewModel.type;
	}

	@computed
	get person() {
		return this.sagaContext.appState.person;
	}

	uiConfiguration: Models.WebGivingUIConfiguration;

	@observable
	scrollStateStore:ScrollStateStore = getScrollStateStore();

	@observable
	theme: Models.Theme;

	@observable
	brandingPackage: Models.BrandingPackage | null;

	userActionChannel: Channel<WebGivingUserAction> = userActionChannel();

	@observable
	sagaContext: IWebGivingSagaContext;

	@observable
	isAccountDetailsOpen: boolean;

	@observable
	notification: NotificationViewModel;

	@observable
	isAppLoading: boolean;

	webGivingPushpayInfo: Models.WebGivingPushpayInfo;

	@observable
	visibleModal: ModalType | null = null;

	@observable
	resendCodeDisabled = observable.box(false);

	@observable
	viewData: WebGivingViewModel;

	@observable
	private _currentPageViewModel: WebGivingPageViewModel;

	@observable
	unavailableLanguages:  Record<string, Models.Language>;

	private history = new HistoryAdapter('pp-wg-key');

	hasTranslationEnabled: boolean;

	@observable
	animationType: Models.ThankYouAnimationType;

	@observable
	applePayAvailable: boolean;

	constructor(viewData: WebGivingViewModel, disableValidation: boolean = false) {
		this.viewData = viewData;
		this.theme = viewData.Theme;
		this.brandingPackage = viewData.BrandingPackage;
		this.history.start(() => this.sagaContext.executingBlockingAction);
		this.webGivingPushpayInfo = viewData.PushpayInfo;
		this.hasTranslationEnabled = viewData.MerchantInfo.IsEntitledToLanguageSelector;
		this.unavailableLanguages = {};
		this.uiConfiguration = viewData.UIConfiguration;

		const personId = viewData.Person ? viewData.Person.SignedPersonId : undefined;

		const rememberedAmount = getUserRememberedAmount(viewData.MerchantInfo.MerchantId, personId);
		if (!rememberedAmount) {
			clearUserRemembered();
		}

		this.sagaContext = {
			mainViewModel: this,
			userActionChannel: this.userActionChannel,
			appState: defaultAppState(viewData, rememberedAmount),
			dataService: sagaDataService(() => this.sagaContext.appState),
			disableValidation,
			reportError: this.reportError,
		};

		if (viewData.PreconfiguredPaymentDetails && viewData.PreconfiguredPaymentDetails.InvalidPreConfiguredLink) {
			this.openModal(ModalType.InvalidPreconfiguredLink);
		}

		if (viewData.PreconfiguredPaymentDetails
			&& viewData.PreconfiguredPaymentDetails.FailedToResolveFundId
			&& !viewData.PreconfiguredPaymentDetails.InvalidPreConfiguredLink) {
			this.openModal(ModalType.FailedToResolveFund);
		}

		runSaga({}, (context) => webGivingSaga(context), this.sagaContext);

		if (this.uiConfiguration.AttemptGivePageSkip || this.uiConfiguration.SkipGivePage) {
			this.userActionChannel.put(new WebGivingUserAction.SelectInitialPage());
		} else {
			this.openPage(new WebGivingPageViewModel.Give(this.sagaContext.appState.paymentState, this.viewData.MerchantInfo, this.viewData.BrandingPackage));
			this.setCurrentPageShouldBeVisible();
		}
		
		this.userActionChannel.put(new WebGivingUserAction.VerifyApplePaySDK());
		this.history.listen(() => {
			if (typeof this.visibleModal === 'number') {
				this.closeModal();
			} else {
				this.userActionChannel.put(new WebGivingUserAction.PreviousPage());
			}
		});
	}

	@action.bound
	redirectToRedirectUrlFromSuccessPage(url: string) {
		GoogleAnalyticsWrapper.pageViewWithLanguage(WebGivingPage[WebGivingPage.PaymentSuccess], getLanguageWithFallback(this.hasTranslationEnabled).TwoLetterIsoCode);

		this.raiseAction(new WebGivingUserAction.RecordPageStats(url));

		document.location.href = url;
	}

	@action.bound
	openPage(viewModel: WebGivingPageViewModel) {
		const { disableValidation } = this.sagaContext;

		if (this._currentPageViewModel) {
			this.pushPageVisitToHistory(viewModel);
		}

		if (this.notification) {
			this.notification.dismiss();
		}
		this.setIsAccountDetailsOpen(false);

		viewModel.setDisableValidation(disableValidation);
		this._currentPageViewModel = viewModel;

		GoogleAnalyticsWrapper.pageViewWithLanguage(WebGivingPage[viewModel.type], getLanguageWithFallback(this.hasTranslationEnabled).TwoLetterIsoCode);

		rg4js('trackEvent', { type: 'pageView', path: `/${WebGivingPage[viewModel.type]}` });

		this.raiseAction(new WebGivingUserAction.RecordPageStats());
	}

	@action.bound
	openModal(modalType: ModalType) {
		this.visibleModal = modalType;
		this.history.push(false);
	}

	@action.bound
	closeModal() {
		this.visibleModal = null;

		this.pushPageVisitToHistory(this._currentPageViewModel);
	}

	@action.bound
	setIsAccountDetailsOpen(state: boolean) {
		this.isAccountDetailsOpen = state;
	}

	setCurrentPageShouldBeVisible = () => {
		this.currentPageViewModel.setPageShouldBeVisible();
	}

	@action.bound
	showNotification(notification: NotificationViewModel) {
		this.notification = notification;
	}

	@action.bound
	showAnimation(type: Models.ThankYouAnimationType) {
		this.animationType = type;
	}

	@action.bound
	hideAnimation() {
		this.animationType = Models.ThankYouAnimationType.None;
	}

	@action.bound
	failedToLoadLanguage(notification: NotificationViewModel, language: Models.Language) {
		this.notification = notification;
		this.isAppLoading = false;
		this.unavailableLanguages[language.TwoLetterIsoCode] = language;
	}

	@action.bound
	setAppLoading(isLoading: boolean) {
		this.isAppLoading = isLoading;
	}

	@action.bound
	setApplePayAvailable(available: boolean) {
		this.applePayAvailable = available;
	}

	@action.bound
	raiseAction(action: WebGivingUserAction) {
		this.userActionChannel.put(action);
	}

	@action.bound
	reportError(error: any, customData?: any) {
		const errorInfo = getErrorInfo(error);

		if (typeof errorInfo.resetPageReason === 'number') {
			this.currentPageViewModel.error = {
				errorType: WebGivingErrorType.ShouldReloadPage,
				reason: errorInfo.resetPageReason,
				paymentLabel: this.viewData.MerchantInfo.PaymentLabel,
				resetPasswordUrl: this.viewData.PushpayInfo.ResetPasswordUrl,
			};
			this.currentPageViewModel.isBlocked = true;
		} else {
			this.currentPageViewModel.error = {
				errorType: WebGivingErrorType.Generic,
				message: errorInfo.message || defaultErrorMessage,
			};
		}

		reportError(errorInfo.error, this.currentPage, customData);
	}

	private pushPageVisitToHistory(viewModel: WebGivingPageViewModel) {
		const { appState } = this.sagaContext;
		const firstPayment = !appState.previousPaymentThankYouPageDetails;
		const isFirstPage = firstPayment && viewModel.type === WebGivingPage.Give;

		this.history.push(isFirstPage);
	}
}

export function reportError(error: any, currentPage: WebGivingPage, customData?: any) {
	if (error instanceof PostError) {
		if (error.client.status !== 500) {
			// let's ignore all the errors expect 500s because they are not unexpected
			return;
		}
	}

	if (error instanceof ConfirmGiftPostError) {
		// let's show different message if error occured while confirming payment
		return;
	}

	window.reportUnhandledRejection(error, {
		...customData,
		cookiesEnabled: window.navigator.cookieEnabled,
		currentPage: WebGivingPage[currentPage],
	});
}
